/**
 * derived.ts
 *
 * Values computed by walking the nested route tree from routes.ts.
 *
 * Nothing here is hand-coded — every export is derived automatically so there
 * is no risk of the tree and these helpers falling out of sync.
 *
 * Exports
 * ───────
 * getRouteBySegment  Look up a RouteDefinition by its raw URL segment (O(1)).
 *                    Used by the breadcrumb builder to resolve label + icon.
 *
 * portalApps         Ordered list of portal-app metadata cards shown in the
 *                    AppHub and used by the AppBar for active-tab detection.
 *
 * appHomeBreadcrumb  Static breadcrumb node for the portal root — the "home"
 *                    crumb that always appears first in every breadcrumb trail.
 */

import type { PortalAppDefinition, BreadcrumbItem, NavItem } from '@/shared/types';

import { getRoutesSafely } from '../maps';
import type { RouteDefinition } from '../types';

const nullIcon = () => null;

const fallbackRoutes = {
  root: {
    segment: '',
    path: '/',
    label: 'Client Matter Administration Portal',
    icon: nullIcon,
  },
  engagementLetterDatabase: {
    segment: 'engagement-letter-database',
    path: '/engagement-letter-database',
    label: 'Engagement Letter Database',
    icon: nullIcon,
    isPortalApp: true,
    navItems: [
      { id: 'eld-home', name: 'Home', path: '/engagement-letter-database', icon: nullIcon },
      { id: 'eld-client', name: 'Engagement Letters', path: '/engagement-letter-database/client/:clientId', icon: nullIcon },
      { id: 'eld-waiver', name: 'Waiver Letters', path: '/engagement-letter-database/waiver/:waiverId', icon: nullIcon },
      { id: 'eld-related-client', name: 'Related Clients', path: '/engagement-letter-database/related-client/:relatedClientId', icon: nullIcon },
    ],
    home: {
      segment: '',
      path: '/engagement-letter-database',
      label: 'Home',
      icon: nullIcon,
    },
    client: {
      segment: 'client',
      path: '/engagement-letter-database/client/:clientId',
      label: 'Client',
      icon: nullIcon,
    },
    relatedClient: {
      segment: 'related-client',
      path: '/engagement-letter-database/related-client/:relatedClientId',
      label: 'Related Client',
      icon: nullIcon,
    },
  },
  app: {
    segment: 'app',
    path: '/app',
    label: 'App',
    icon: nullIcon,
  },
} satisfies Record<string, unknown>;

const safeRoutes = getRoutesSafely();
const routeTree = (safeRoutes ?? fallbackRoutes) as unknown as Record<string, unknown>;

// ─── Private: tree walker ─────────────────────────────────────────────────────
//
// Recursively collects every node in the route tree that looks like a
// RouteDefinition (has segment / path / label / icon).  The result is used
// to build both the segment lookup table and the portalApps list.

function collectNodes(
  node: Record<string, unknown>,
  visited: WeakSet<Record<string, unknown>> = new WeakSet(),
): RouteDefinition[] {
  if (visited.has(node)) {
    return [];
  }

  visited.add(node);

  const collected: RouteDefinition[] = [];
  for (const value of Object.values(node)) {
    if (isRouteDefinition(value)) {
      const routeNode = value as unknown as Record<string, unknown>;

      if (visited.has(routeNode)) {
        continue;
      }

      collected.push(value, ...collectNodes(routeNode, visited));
    }
  }
  return collected;
}

function isRouteDefinition(value: unknown): value is RouteDefinition {
  return (
    value !== null &&
    typeof value === 'object' &&
    'segment' in value &&
    'path' in value &&
    'label' in value &&
    'icon' in value
  );
}

// ─── All nodes, collected once ────────────────────────────────────────────────

const allNodes: RouteDefinition[] = collectNodes(routeTree);

// ─── Segment → RouteDefinition  ───────────────────────────────────────────────

const segmentLookup: Record<string, RouteDefinition> = Object.fromEntries(
  allNodes.filter((node) => node.segment !== '').map((node) => [node.segment, node]),
);

/**
 * Returns the RouteDefinition for a given URL segment string, or `undefined`
 * if the segment is not registered in the route tree.
 *
 * @example
 * getRouteBySegment('engagement-letter-database') // → { label: 'Engagement Letter Database', ... }
 * getRouteBySegment('unknown')           // → undefined
 */
export const getRouteBySegment = (segment: string): RouteDefinition | undefined => segmentLookup[segment];

// ─── Portal apps (AppHub cards + AppBar detection) ───────────────────────────

/**
 * Ordered list of portal applications.
 * Drives the AppHub card grid and the AppBar's active-app detection.
 */
export const portalApps: PortalAppDefinition[] = allNodes
  .filter((node): node is RouteDefinition & { isPortalApp: true } => node.isPortalApp === true)
  .map((node) => {
    const navItems: NavItem[] | undefined = Array.isArray(node.navItems)
      ? (node.navItems as readonly NavItem[]).map((navItem): NavItem => ({ ...navItem }))
      : undefined;

    return {
      id: node.segment,
      label: node.label,
      title: node.label,
      path: node.path,
      icon: node.icon,
      navItems,
    };
  });

// ─── Root breadcrumb ─────────────────────────────────────────────────────────

/**
 * Static breadcrumb for the portal root.
 * Always rendered as the first crumb in every breadcrumb trail.
 */
export const appHomeBreadcrumb: BreadcrumbItem = {
  key: 'apps-root',
  label: safeRoutes?.root?.label ?? fallbackRoutes.root.label,
  path: safeRoutes?.root?.path ?? fallbackRoutes.root.path,
  icon: (safeRoutes?.root?.icon ?? fallbackRoutes.root.icon) as BreadcrumbItem['icon'],
  isCurrent: false,
};
